import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, CheckCircle2, XCircle, Clock, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { sendClaimApproval } from '@/components/email/sendEmail';

export default function ClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denialReason, setDenialReason] = useState('');
  const [requestInfoReason, setRequestInfoReason] = useState('');

  useEffect(() => {
    loadClaimDetail();
  }, [claimId]);

  const loadClaimDetail = async () => {
    try {
      await api.auth.me();
      
      const claimsData = await api.entities.Claim.filter({ id: claimId });
      if (claimsData && claimsData.length > 0) {
        setClaim(claimsData[0]);
        
        const employeesData = await api.entities.Employee.filter({ id: claimsData[0].employee_id });
        if (employeesData && employeesData.length > 0) {
          setEmployee(employeesData[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load claim:', err);
      toast.error('Failed to load claim details');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClaim = async () => {
    try {
      const user = await api.auth.me();
      
      const auditHistory = claim.audit_history ? JSON.parse(claim.audit_history) : [];
      auditHistory.push({
        action: 'approved',
        by: user.full_name || user.email,
        date: new Date().toISOString(),
        note: `Claim approved by ${user.full_name || user.email}`
      });
      
      await api.entities.Claim.update(claim.id, {
        status: 'approved',
        approved_date: new Date().toISOString(),
        processed_by: user.email,
        processed_date: new Date().toISOString(),
        audit_history: JSON.stringify(auditHistory)
      });

      if (employee) {
        try {
          const emailData = sendClaimApproval(employee.user_email, {
            employeeName: `${employee.first_name} ${employee.last_name}`,
            claimAmount: Number(claim.claim_amount).toFixed(2),
            claimType: claim.claim_type,
            claimDate: format(new Date(claim.claim_date), 'MMMM d, yyyy'),
            approvedDate: format(new Date(), 'MMMM d, yyyy'),
            claimId: claim.id,
            approvedBy: user.full_name || user.email,
            description: claim.description
          });

          await api.integrations.Core.SendEmail({
            to: emailData.to,
            subject: emailData.subject,
            body: emailData.html
          });
        } catch (error_) {
          console.error('Email notification failed:', error_);
        }
      }
      
      toast.success(`✅ Claim approved! ${employee ? 'Employee has been notified.' : ''}`);
      navigate('/ClaimsManagement');
    } catch (err) {
      console.error(err);
      toast.error('Failed to approve claim');
    }
  };

  const handleDenyClaim = async () => {
    if (!denialReason.trim() || denialReason.trim().length < 10) {
      toast.error('Please provide a detailed reason (at least 10 characters)');
      return;
    }

    try {
      const user = await api.auth.me();
      
      const auditHistory = claim.audit_history ? JSON.parse(claim.audit_history) : [];
      auditHistory.push({
        action: 'denied',
        by: user.full_name || user.email,
        date: new Date().toISOString(),
        note: `Claim denied: ${denialReason.trim()}`
      });
      
      await api.entities.Claim.update(claim.id, {
        status: 'denied',
        denial_reason: denialReason.trim(),
        processed_by: user.email,
        processed_date: new Date().toISOString(),
        audit_history: JSON.stringify(auditHistory)
      });
      
      toast.success('Claim denied - employee will be notified');
      navigate('/ClaimsManagement');
    } catch (err) {
      console.error(err);
      toast.error('Failed to deny claim');
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      const user = await api.auth.me();
      
      const auditHistory = claim.audit_history ? JSON.parse(claim.audit_history) : [];
      auditHistory.push({
        action: 'paid',
        by: user.full_name || user.email,
        date: new Date().toISOString(),
        note: `Claim marked as paid by ${user.full_name || user.email}`
      });
      
      await api.entities.Claim.update(claim.id, {
        status: 'paid',
        paid_date: new Date().toISOString(),
        audit_history: JSON.stringify(auditHistory)
      });
      
      toast.success('Claim marked as paid');
      navigate('/ClaimsManagement');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update claim');
    }
  };

  const handleRequestMoreInfo = async () => {
    if (!requestInfoReason.trim()) {
      toast.error('Please specify what additional information is needed');
      return;
    }

    try {
      const user = await api.auth.me();
      
      const auditHistory = claim.audit_history ? JSON.parse(claim.audit_history) : [];
      auditHistory.push({
        action: 'info_requested',
        by: user.full_name || user.email,
        date: new Date().toISOString(),
        note: `Additional information requested: ${requestInfoReason.trim()}`
      });
      
      await api.entities.Claim.update(claim.id, {
        status: 'pending',
        denial_reason: `More info needed: ${requestInfoReason.trim()}`,
        processed_by: user.email,
        processed_date: new Date().toISOString(),
        audit_history: JSON.stringify(auditHistory)
      });
      
      toast.success('Request sent - employee will be notified to provide more information');
      navigate('/ClaimsManagement');
    } catch (err) {
      console.error(err);
      toast.error('Failed to request more info');
    }
  };

  const handleUnapprove = async () => {
    if (!denialReason.trim() || denialReason.trim().length < 10) {
      toast.error('Please provide a detailed reason (at least 10 characters)');
      return;
    }

    try {
      const user = await api.auth.me();
      
      const auditHistory = claim.audit_history ? JSON.parse(claim.audit_history) : [];
      auditHistory.push({
        action: 'unapproved',
        by: user.full_name || user.email,
        date: new Date().toISOString(),
        note: `Approval revoked: ${denialReason.trim()}`
      });
      
      await api.entities.Claim.update(claim.id, {
        status: 'pending',
        approved_date: null,
        denial_reason: `Approval revoked: ${denialReason.trim()}`,
        processed_by: user.email,
        processed_date: new Date().toISOString(),
        audit_history: JSON.stringify(auditHistory)
      });
      
      toast.success('Claim approval revoked - returned to pending status');
      navigate('/ClaimsManagement');
    } catch (err) {
      console.error(err);
      toast.error('Failed to unapprove claim');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Approved' },
      denied: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Denied' },
      paid: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Paid' },
      cancelled: { color: 'bg-slate-100 text-slate-600', icon: XCircle, label: 'Cancelled' }
    };
    
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <Badge className={style.color}>
        <Icon className="w-3 h-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const getAuditDotColor = (action) => {
    switch (action) {
      case 'approved':
        return 'bg-green-500';
      case 'denied':
        return 'bg-red-500';
      case 'paid':
        return 'bg-emerald-500';
      case 'unapproved':
        return 'bg-amber-500';
      default:
        return 'bg-blue-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Claim not found</p>
        <Button onClick={() => navigate('/ClaimsManagement')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Claims
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/ClaimsManagement')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Claim Details</h1>
          <p className="text-slate-500 mt-1">Review and process claim</p>
        </div>
        {getStatusBadge(claim.status)}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Main Details */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Claim Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-slate-500 text-sm">Employee</p>
                <p className="font-medium">{employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-sm">Amount</p>
                <p className="font-medium text-lg">${Number(claim.claim_amount).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-slate-500 text-sm">Date</p>
                <p className="font-medium">{claim.claim_date ? format(new Date(claim.claim_date), 'MMM d, yyyy') : 'N/A'}</p>
              </div>
            </div>

            <div>
              <p className="text-slate-500 text-sm mb-2">Type</p>
              <Badge variant="outline">{claim.claim_type}</Badge>
            </div>
            
            <div>
              <p className="text-slate-500 text-sm mb-1">Description</p>
              <p className="text-slate-900">{claim.description || 'No description provided'}</p>
            </div>

            {claim.receipt_url && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900 mb-2">📎 Receipt Attached</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const url = claim.receipt_url.startsWith('http') 
                      ? claim.receipt_url 
                      : `http://localhost:3001${claim.receipt_url}`;
                    window.open(url, '_blank');
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Receipt
                </Button>
              </div>
            )}

            {!claim.receipt_url && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">⚠️ No receipt attached to this claim</p>
              </div>
            )}

            {claim.processed_by && (
              <div className="text-sm text-slate-600">
                <strong>Processed by:</strong> {claim.processed_by}
                {claim.processed_date && new Date(claim.processed_date).getTime() > 0 && (
                  <span> on {format(new Date(claim.processed_date), 'MMM d, yyyy h:mm a')}</span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit History Sidebar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activity History</CardTitle>
          </CardHeader>
          <CardContent>
            {claim.audit_history && claim.audit_history !== 'null' && JSON.parse(claim.audit_history).length > 0 ? (
              <div className="space-y-3">
                {JSON.parse(claim.audit_history)
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((entry) => (
                  <div key={`${entry.date}-${entry.action}-${entry.by}`} className="flex items-start gap-2 text-xs pb-3 border-b border-slate-100 last:border-0">
                    <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${getAuditDotColor(entry.action)}`} />
                    <div className="flex-1">
                      <p className="text-slate-700 font-medium">{entry.note}</p>
                      <p className="text-slate-500 mt-1">{format(new Date(entry.date), 'MMM d, h:mm a')}</p>
                      <p className="text-slate-400">by {entry.by}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No activity history yet</p>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card className="md:col-span-3">
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            {claim.status === 'approved' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-medium">✓ This claim has been approved</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Revoke Approval (Optional)</Label>
                    <Textarea
                      value={denialReason}
                      onChange={(e) => setDenialReason(e.target.value)}
                      placeholder="Explain why approval is being revoked..."
                      rows={3}
                      className="mt-2"
                    />
                    <Button
                      variant="destructive"
                      onClick={handleUnapprove}
                      disabled={!denialReason.trim() || denialReason.trim().length < 10}
                      className="mt-2"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Revoke Approval
                    </Button>
                  </div>
                  <div>
                    <Button
                      onClick={handleMarkAsPaid}
                      className="bg-emerald-500 hover:bg-emerald-600 w-full h-full"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Paid
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {claim.status === 'pending' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Deny Claim</Label>
                    <Textarea
                      value={denialReason}
                      onChange={(e) => setDenialReason(e.target.value)}
                      placeholder="Provide a clear reason for denial..."
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Request More Information</Label>
                    <Textarea
                      value={requestInfoReason}
                      onChange={(e) => setRequestInfoReason(e.target.value)}
                      placeholder="Specify what additional info is needed..."
                      rows={3}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleApproveClaim}
                    className="bg-green-500 hover:bg-green-600 flex-1"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Approve Claim
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRequestMoreInfo}
                    disabled={!requestInfoReason.trim()}
                    className="flex-1"
                  >
                    Request Info
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDenyClaim}
                    disabled={!denialReason.trim() || denialReason.trim().length < 10}
                    className="flex-1"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Deny Claim
                  </Button>
                </div>
              </div>
            )}

            {(claim.status === 'paid' || claim.status === 'denied' || claim.status === 'cancelled') && (
              <div className={`p-4 rounded-lg ${
                claim.status === 'paid' ? 'bg-emerald-50 border border-emerald-200' :
                'bg-slate-50 border border-slate-200'
              }`}>
                <p className="text-sm text-slate-700">
                  This claim has been {claim.status}. No further actions available.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
